import { useQuery, useQueryClient } from 'react-query'
import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'

const fetchOrganizations = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/organisations`)
  return data
}

const fetchPipelines = async ({
  page = 1,
  page_size = 10,
  order_by = 'created_at',
  order_direction = 'desc',
  status,
  organization_id,
}) => {
  const ordering = order_direction === 'desc' ? `-${order_by}` : order_by
  const params = {
    page,
    page_size,
    ordering,
    ...(status && { status }),
    ...(organization_id && { organization_id }),
  }

  const { data } = await axios.get(`${API_BASE_URL}/pipeline-task-history/pipelines/`, {
    params,
  })
  return data
}

export const fetchPipelineTasks = async (
  pipelineId,
  page = 1,
  page_size = 10,
  order_by = 'enqueued_at',
  order_direction = 'desc',
) => {
  const ordering = order_direction === 'desc' ? `-${order_by}` : order_by
  const { data } = await axios.get(`${API_BASE_URL}/pipeline-task-history/pipelines/${pipelineId}/tasks/`, {
    params: {
      page,
      page_size,
      ordering,
    },
  })
  return data
}

export const rerunPipelineTask = async (pipelineId, taskId) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/pipeline-task-history/pipelines/${pipelineId}/tasks/${taskId}/rerun/`,
  )
  return data
}

export const useOrganizations = () => {
  return useQuery('organizations', fetchOrganizations, {
    refetchOnWindowFocus: false,
  })
}

export const usePipelines = (
  page = 1,
  page_size = 10,
  order_by = 'created_at',
  order_direction = 'desc',
  status,
  organization_id,
) => {
  const queryClient = useQueryClient()

  const query = useQuery(
    ['pipelines', page, page_size, order_by, order_direction, status, organization_id],
    () => fetchPipelines({ page, page_size, order_by, order_direction, status, organization_id }),
    {
      refetchOnWindowFocus: true,
      keepPreviousData: true,
    },
  )

  const refresh = async () => {
    await queryClient.refetchQueries(['pipelines', page, page_size, order_by, order_direction, status, organization_id])
    await queryClient.resetQueries(['pipelines', page, page_size, order_by, order_direction, status, organization_id])
  }

  return { query, refresh }
}

export const usePipelineTasks = (
  pipelineId,
  page = 1,
  page_size = 10,
  order_by = 'enqueued_at',
  order_direction = 'desc',
) => {
  const queryClient = useQueryClient()

  const query = useQuery(
    ['pipeline-tasks', pipelineId, page, page_size, order_by, order_direction],
    () => fetchPipelineTasks(pipelineId, page, page_size, order_by, order_direction),
    {
      refetchOnWindowFocus: true,
      keepPreviousData: true,
    },
  )

  const refresh = async () => {
    await queryClient.refetchQueries(['pipeline-tasks', pipelineId, page, page_size, order_by, order_direction])
    await queryClient.resetQueries(['pipeline-tasks', pipelineId, page, page_size, order_by, order_direction])
  }

  return { query, refresh }
}

const fetchPipeline = async (pipelineId) => {
  const { data } = await axios.get(`${API_BASE_URL}/pipeline-task-history/pipelines/${pipelineId}/`)
  return data
}

export const usePipeline = (pipelineId) => {
  return useQuery(['pipeline', pipelineId], () => fetchPipeline(pipelineId), {
    refetchOnWindowFocus: false,
  })
}
